
<template>
  <div class="tab-user-list">

    <ui-table-wrapper
      ref="table"
      :provider="provider"
      :fields="fields"
      :size="UI.SIZE.MEDIUM"
      primary-key="compte_id"
      striped
      hover
      @row-clicked="({ item }) => openDetailsIntent(item)"
    >
      <template
        v-if="providerCallCount === 0"
        #table-busy
      >
        <layout-placeholder-table-body
          :count="context.perPage"
          striped
        />
      </template>
      <template #empty>
        <ui-well class="text-align-center">
          Aucun résultat
        </ui-well>
      </template>

      <template #cell(actions)="data">
        <div class="tab-user-list__actions__inner">

          <ui-buffered-pending-icon
            :is-pending="isRowPending(data.item.compte_id)"
            :type="UI.SPINNER_TYPE.BORDER"
          >
            <template #default="{ onPreBufferPending, onBufferPending }">
              <div :id="`parameters-actifs-list-dropdown-${data.item.compte_id}`">
                <ui-dropdown-wrapper
                  :menu-variant="UI.VARIANT.PRIMARY"
                  :status="onPreBufferPending ?
                    UI.STATUS.IDLED :
                    (
                      onBufferPending ?
                        UI.STATUS.DISABLED :
                        UI.STATUS.ENABLED
                    )
                  "
                  placement="left-start"
                >
                  <template #button-content>
                    <ui-button-icon
                      name="more_vert"
                      :variant="UI.VARIANT.DARK"
                      :size="UI.SIZE.SMALL"
                      square
                      ripple
                    />
                  </template>
                  <template #default>

                    <b-dropdown-text
                      v-if="data.item.is_ghs"
                      class="bg-color-gray-99 mb-1 py-1"
                    >
                      <ui-wad class="font-size-80 font-family-heading line-height-1.3 color-gray-deep font-weight-bold">
                        Cet utilisateur est
                        <ui-badge-common
                          class="ml-0.5 font-size-12px"
                          variant="info"
                        >GHS</ui-badge-common>
                      </ui-wad>
                      <ui-wad class="font-size-80 font-family-heading line-height-1.3 color-gray-dark font-weight-bold mt-0.5">
                        Les fonctionnalités de modification <br>et de suppression sont restreintes
                      </ui-wad>
                    </b-dropdown-text>

                    <b-dropdown-text
                      v-if="data.item.owner"
                      class="bg-color-gray-99 mb-1 py-1"
                    >
                      <ui-wad class="font-size-80 font-family-heading line-height-1.3 color-gray-deep font-weight-bold">
                        Cet utilisateur est
                        <ui-badge-common
                          class="ml-0.5 font-size-12px"
                          variant="purple"
                        >Super Admin</ui-badge-common>
                      </ui-wad>
                      <ui-wad class="font-size-80 font-family-heading line-height-1.3 color-gray-dark font-weight-bold mt-0.5">
                        La suppression est impossible
                      </ui-wad>
                    </b-dropdown-text>
                    <b-dropdown-item
                      :disabled="
                        data.item.is_ghs ||
                        !hasACL(ACLS.PERMISSION_MANAGEMENT_UPDATE)
                      "
                      :to="getDetailsRoute(data.item.compte_id)"
                    >
                      <ui-icon
                        name="edit"
                      />
                      Modifier l'accès
                    </b-dropdown-item>
                    <b-dropdown-item
                      :disabled="
                        data.item.is_ghs || data.item.owner ||
                        !hasACL(ACLS.PERMISSION_MANAGEMENT_DELETE)
                      "
                      @click="() => deletionIntent(data.item.compte_id)"
                    >
                      <ui-icon
                        name="delete"
                      />
                      Supprimer l'utilisateur
                    </b-dropdown-item>
                  </template>
                </ui-dropdown-wrapper>
              </div>
            </template>
          </ui-buffered-pending-icon>

          <ui-popover-wrapper
            :ref="`popover-${data.item.compte_id}`"
            :target="`parameters-actifs-list-dropdown-${data.item.compte_id}`"
            placement="left"
            size="fluid"
            no-animation
            manual
          >
            <template #popover>
              <ui-wad class="d-flex gap-3 align-items-center px-0.5">
                <ui-wad class="font-weight-light line-height-1.2">
                  La suppression retire définitivement l'accès de cet utilisateur<br>
                  et supprime ses données
                </ui-wad>
                <ui-button-icon
                  name="delete"
                  square
                  :elevation="UI.ELEVATION.LEVEL_2"
                  :variant="UI.VARIANT.DARK"
                  @click="() => deletionConfirmation(data.item.compte_id)"
                />
              </ui-wad>
            </template>
          </ui-popover-wrapper>

        </div>
      </template>

      <template #cell(user)="data">
        {{ data.item.fullname }}<span
          class="color-gray-dark">&nbsp;&mdash;&nbsp;{{ data.item.email }}</span>
        <ui-badge-common
          v-if="data.item.owner"
          class="ml-1 font-size-13px"
          variant="purple"
        >Super Admin</ui-badge-common>
        <ui-badge-light
          v-if="data.item.payant"
          class="ml-1 font-size-13px color-primary"
          :variant="UI.VARIANT.PRIMARY"
        >
          <ui-icon
            name="key"
          />
        </ui-badge-light>
        <ui-tooltip-wrapper
          v-if="data.item.is_ghs"
          class="tab-user-list__ghs-badge"
        >
          <template #tooltip>
            <div style='margin-bottom: 6px; text-align: center'>
              Utilisateur·rice GHS
            </div>
            <div style='font-size: 80%; line-height: 1.4'>
              non comptabilisé comme siège occupé
            </div>
          </template>
          <template #default>
            <ui-badge-common
              class="ml-1"
              variant="info"
            >
              <ui-icon
                name="verified_user"
              />GHS
            </ui-badge-common>
          </template>
        </ui-tooltip-wrapper>
      </template>

      <template #cell(companyCounter)="data">
        <ui-wad v-if="data.item.all_societes">
          <span class="font-size-90 line-height-0 d-inline-block font-weight-normal text-transform-uppercase ">Toutes</span>
        </ui-wad>

        <ui-wad v-if="!data.item.all_societes">
          <span class="font-size-100 line-height-0 font-family-monospace d-inline-block font-weight-normal">{{
            data.item.nb_societe
          }}</span>
          <ui-blank class="mr-0.5" />
          <span class="font-size-80 font-weight-normal text-transform-uppercase color-gray-dark">
            {{
              data.item.nb_societe > 1 ?
                `societés` :
                `societé`
            }}
          </span>
        </ui-wad>
      </template>

    </ui-table-wrapper>


    <div class="tab-user-list__footer">

      <ui-blank />

      <ui-wad class="d-flex align-items-center justify-content-center">
        <ui-table-pagination
          v-model="context.currentPage"
          :total-rows="context.total"
          :per-page="context.perPage"
        />
      </ui-wad>

      <ui-wad class="d-flex align-items-center gap-1 justify-content-flex-end">
        <ui-form-select
        v-model="context.perPage"
        :options="perPageOptions"
        :size="UI.SIZE.SMALL"
        class="w-auto"
        />
        <ui-wad class="font-size-90 font-weight-light">
          résultats par page
        </ui-wad>
      </ui-wad>

    </div>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/table/members

.tab-user-list
  $self: &

  padding: $layout-spacer-y $layout-spacer-x

  &__footer
    margin-top: $layout-spacer-y
    display: grid
    grid-template-columns: repeat(3, 1fr)
    align-items: center

  /////////////////////////////////


  &__ghs-badge
    display: inline-block
    cursor: default

    .ui-icon
      // (micro managing peculiar Material Design Icon height)
      $offset-y: -1.5px

      font-size: 14px
      line-height: 0
      transform: translateY($offset-y)
      margin-right: space-x(.5)

    .ui-badge-common
      font-family: $font-family-monospace
      font-size: 13px
      letter-spacing: 0.8px

  /////////////////////////////////

  $actions-column-width: 44px
  $date-column-width: 150px
  $count-column-width: 240px

  :deep(.b-table.table)
    @include table-presets

    /// Deletion UI
    ///   - kept row strategy
    ///   - opacity with slighter darkened background, echoing disabled state
    ///   - removing events via CSS
    #{ $self }__deleted-row
      opacity: 0.1
      pointer-events: none
      transition: $transition-base
      background-color: $gray-95

    /// Columns
    #{ $self }__actions
      width: $actions-column-width

    #{ $self }__count
      width: $date-column-width

    #{ $self }__date
      width: $date-column-width

    #{ $self }__identity
      padding-left: space-x(2)

    /// Cells
    tbody

      #{ $self }__actions
        @include table-cell-presets

      #{ $self }__date
        word-spacing: -1.5px
        color: $gray-deep

</style>

<script lang="ts">
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useDataOnlyNavigationContext } from '~/composables/layout/navigation'
import { useTableProvider, type Context } from '~/composables/layout/table-provider'
import { getDetailsRoute } from '~/stores/client/admin'
import { type UserActive } from '~/stores/client/admin/active'
import { TAB_MENU, useStore_client_admin_list, type ListFilters } from '~/stores/client/admin/list'
import type { UiPopoverWrapper, UiTableWrapper } from '#components'
import { wrap as wrapInput } from '~/utils/infra/common/types'
import Console from '~/utils/lib/core/console'
import { append, clone, equals, includes, Maybe, pipe_, propOr, reject, replace, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { parseDatetime } from '~/utils/lib/core/datetime'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { useToastBuilder } from '~/composables/layout/toast'
import { useStore_client_admin_active } from '~/stores/client/admin/active'
import { useLegacyTemplateRef } from '~/composables/core/dom'
import { querySelector } from '~/utils/lib/core/dom'
import { hasACL, useAccessControl } from '~/composables/layout/access-control'
import { extractRouteCompanyCode } from '~/stores/layout/navigation'
import { useLinkHandler } from '~/composables/core/link-handler'

const console = new Console('component:user-list')

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.DATA_ONLY,
      menuItem: MENU_ITEM.PARAMETERS_USER_MANAGEMENT,
    })
    const navigationContext = useDataOnlyNavigationContext()

    const ref_table
      = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')

    const defaultProviderValue = {
      context: {
        perPage: 50
      }
    }

    const tableProvider = useTableProvider<UserActive, ListFilters>(
      ref_table,
      defaultProviderValue
    )

    const ToastBuilder = useToastBuilder()

    const refSelector = useLegacyTemplateRef()

    const store_admin_list = useStore_client_admin_list()
    const store_active_list = useStore_client_admin_active()

    store_admin_list.setTabItem(TAB_MENU.ACTIVE_USER_LIST)

    return {
      ToastBuilder,
      ...useAccessControl(),
      ...useLinkHandler(),
      ...navigationContext,
      ...tableProvider,
      refSelector,
      store_active_list,
      store_admin_list,
    }
  },
  data() {
    return {
      Maybe,
      TAB_MENU,
      fields: [
        {
          key: 'user',
          label: `Utilisateurs`,
          class: 'tab-user-list__identity'
        },
        {
          key: 'companyCounter',
          label: `Sociétés`,
          class: 'tab-user-list__count'
        },
        {
          key: 'created_at',
          label: `Date d'ajout`,
          class: 'tab-user-list__date',
          formatter: ({ value }) => pipe_(
            value,
            parseDatetime(
              {
                inputFormat: undefined,
                outputFormat: 'DD/MM/YY',
              }
            ),
            replace(/\//g, ' / ') // (for "word-spacing" CSS rule)
          ),
        },
        {
          key: 'actions',
          label: '',
          class: 'tab-user-list__actions'
        },
      ],
      perPageOptions: [
        {
          value: 50,
          text: '50'
        },
        {
          value: 100,
          text: '100',
        },
        {
          value: 200,
          text: '200'
        }
      ],
      pendingList: [] as Array<UserActive["compte_id"]>,
    }
  },
  created() {
    /**
     * @desc Providing `store_admin_list.filters` to input request
     */
    this.consumer = (context :Context<ListFilters>) => {
      const options = {
        input: wrapInput(context),
        uuidData: this.fromStructureLevel().structure.uuidData
      }

      options.input.filters = clone(this.store_admin_list.filters)

      return this.store_active_list.list(options)
        .catch(tapCatch(err => {
          console.warn('consumer', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Liste des utilisateurs`)
            .show()
        }))
    }
    /**
     * @desc Deep listening of the `store_admin_list.filters` to trigger
     *       new list update
     */
    this.$watch(
      instance => instance.store_admin_list.filters,
      () => this.refreshTable(),
      {
        immediate: false,
        deep: true,
      }
    )
  },
  methods: {
    openDetailsIntent({ compte_id, is_ghs }) {
      if (!this.hasACL(this.ACLS.PERMISSION_MANAGEMENT_UPDATE))
        return

      if (is_ghs) return

      const route = this.getDetailsRoute(compte_id)

      this.goToRoute(route)
    },
    getDetailsRoute(compte_id) {
      const {
        id,
      } = this.fromStructureLevel().structure
      const company_code = extractRouteCompanyCode(this.$route)

      return getDetailsRoute(id, compte_id, company_code)
    },
    isRowPending(compte_id) {
      return includes(compte_id, this.pendingList)
    },
    deletionIntent(compte_id) {
      this.refSelector<InstanceType<typeof UiPopoverWrapper>>(`popover-${compte_id}`)
        .map(instance => instance.show())
    },
    deletionConfirmation(compte_id) {
      const { uuidData } = this.fromStructureLevel().structure

      this.pendingList = append(compte_id, this.pendingList)

      this.refSelector<InstanceType<typeof UiPopoverWrapper>>(`popover-${compte_id}`)
        .map(instance => instance.hide())

      return sustainedPromise(
          300,
          this.store_active_list.delete({ uuidData, compte_id })
        )
        .then(() => removeRow(compte_id))
        .then(() => this.refreshTable())
        .catch(err => {
          console.warn('deletionConfirmation', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Suppression d'un utilisateur`)
            .show()
        })
        .finally(()  => {
          this.pendingList = reject(
            equals(compte_id),
            this.pendingList
          )
        })
    },
  }
})
/**
 * @function removeRow
 * @param {string} compte_id
 * @return {void}
 */
function removeRow(compte_id) {
  const className = 'tab-user-list__deleted-row'

  // start delete animation
  querySelector(`.tab-user-list tr[data-pk="${compte_id}"]`)
   .map( element => element.classList.add(className) )
}
</script>
